import { isValidElement, ReactElement, ReactNode } from "react";
import {
  AlertTriangle,
  CircleAlert,
  CircleCheck,
  CircleX,
  Info,
  LucideIcon,
  TriangleAlert,
  X,
} from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "../shadcn/alert";
import { Button } from "../shadcn/button";
import { cn } from "@/lib/utils";

type NoxusAlertVariant =
  | "default"
  | "destructive"
  | "warning"
  | "success"
  | "info";
type NoxusAlertSize = "default" | "xs";
type NoxusAlertDirection = "horizontal" | "vertical";
// `banner` and `grid` are the looks Banner and NoxusBanner had, kept pixel for pixel when they
// were merged here; unifying the three is a design decision (.claude/skills/noxus-design-mode/consolidate.md).
type NoxusAlertLook = "default" | "banner" | "grid";

interface NoxusAlertAction {
  label: string | ReactNode;
  onClick: () => void;
  disabled?: boolean;
}

interface NoxusAlertProps {
  title?: ReactNode;
  description?: ReactNode;
  icon?: LucideIcon | ReactElement;
  noIcon?: boolean;
  variant?: NoxusAlertVariant;
  size?: NoxusAlertSize;
  /**
   * Layout direction.
   *  - ``horizontal`` (default): icon + title/description on the left,
   *    action button on the right (single row).
   *  - ``vertical``: icon + title on a header row, description below,
   *    action button at the bottom (left-aligned). Same fonts and
   *    surface stylings as horizontal — only the geometry changes.
   */
  direction?: NoxusAlertDirection;
  look?: NoxusAlertLook;
  action?: NoxusAlertAction;
  dismissible?: boolean;
  onDismiss?: () => void;
  className?: string;
}

const actionButtonVariant: Record<
  NoxusAlertVariant,
  "outline" | "ghostDestructive" | "ghostWarning"
> = {
  default: "outline",
  destructive: "ghostDestructive",
  warning: "ghostWarning",
  success: "outline",
  info: "outline",
};

const actionTextColor: Record<NoxusAlertVariant, string> = {
  default: "",
  destructive: "text-destructive",
  warning: "text-warning-700",
  success: "text-success-700",
  info: "text-info-700",
};

const bannerIcon: Record<NoxusAlertVariant, LucideIcon> = {
  default: Info,
  destructive: CircleX,
  warning: AlertTriangle,
  success: CircleCheck,
  info: Info,
};

const bannerActionVariant: Record<
  NoxusAlertVariant,
  "outline" | "outlineDestructive" | "outlineWarning"
> = {
  default: "outline",
  destructive: "outlineDestructive",
  warning: "outlineWarning",
  success: "outline",
  info: "outline",
};

/**
 * The one way to show an inline notice or a page banner: icon, title, description and an optional action. Use it instead of composing shadcn Alert by hand, Banner or NoxusBanner.
 *
 * @import import { NoxusAlert } from "@/components/UI/NoxusAlert";
 */
const NoxusAlert = ({
  title,
  description,
  icon: ProvidedIcon,
  noIcon = false,
  variant = "default",
  size = "default",
  direction = "horizontal",
  look = "default",
  action,
  dismissible = false,
  onDismiss,
  className,
}: NoxusAlertProps) => {
  const defaultIcon =
    variant === "warning"
      ? TriangleAlert
      : variant === "success"
        ? CircleCheck
        : variant === "info"
          ? Info
          : CircleAlert;
  const providedElement = isValidElement(ProvidedIcon) ? ProvidedIcon : null;
  const Icon =
    ProvidedIcon && !isValidElement(ProvidedIcon) ? ProvidedIcon : defaultIcon;
  const icon = noIcon
    ? null
    : (providedElement ?? <Icon size={16} strokeWidth={1.5} />);

  if (look === "grid") {
    return (
      <Alert variant={variant} className={className}>
        {!noIcon && (providedElement ?? <Icon />)}
        {title && <AlertTitle>{title}</AlertTitle>}
        <AlertDescription>{description}</AlertDescription>
      </Alert>
    );
  }

  if (look === "banner") {
    const BannerIcon = bannerIcon[variant];
    const isSmall = size === "xs";
    const isVertical = direction === "vertical";
    return (
      <Alert
        variant={variant}
        className={cn(
          "relative flex gap-2",
          isVertical
            ? "flex-col items-stretch"
            : "items-center justify-between",
          className,
          isSmall && "text-xs",
          dismissible && "pr-10"
        )}
      >
        <div className="flex min-w-0 items-center gap-2">
          <BannerIcon
            className={cn("shrink-0 self-start", isSmall ? "size-4" : "size-5")}
          />
          <AlertDescription className="min-w-0">
            <div className="font-medium">{title}</div>
            {description && <p className="mt-0.5 font-normal">{description}</p>}
          </AlertDescription>
          {dismissible && (
            <Button
              onClick={onDismiss}
              variant="subtle"
              aria-label="Close button"
              size={isSmall ? "iconxs" : undefined}
              className={cn("absolute right-2", isSmall ? "top-1" : "top-2")}
            >
              <X size={16} strokeWidth={1.25} />
            </Button>
          )}
        </div>
        {action && (
          <div
            className={cn(isVertical && (isSmall ? "mt-1 pl-6" : "mt-1 pl-7"))}
          >
            <Button
              variant={bannerActionVariant[variant]}
              size={isVertical && isSmall ? "sm" : undefined}
              onClick={action.onClick}
              disabled={action.disabled}
            >
              {action.label}
            </Button>
          </div>
        )}
      </Alert>
    );
  }

  // Absolute so it holds the top-right corner without taking part in the
  // layout; the alert reserves room for it with pr-8.
  const dismissButton = dismissible ? (
    <button
      type="button"
      aria-label="Dismiss"
      className={cn(
        "absolute top-2 right-2 cursor-pointer",
        actionTextColor[variant]
      )}
      onClick={onDismiss}
    >
      <X size={16} strokeWidth={1.5} />
    </button>
  ) : null;

  if (direction === "vertical") {
    return (
      <Alert
        variant={variant}
        size={size === "xs" ? "xs" : "default"}
        className={cn("flex flex-col gap-2", dismissible && "pr-8", className)}
      >
        {dismissButton}
        {(icon || title) && (
          <div className="flex items-center gap-2 min-w-0">
            {icon && <div className="flex shrink-0 items-center">{icon}</div>}
            {title && <AlertTitle>{title}</AlertTitle>}
          </div>
        )}
        {description && <AlertDescription>{description}</AlertDescription>}
        {action && (
          <Button
            variant={actionButtonVariant[variant]}
            size={size === "xs" ? "xs" : undefined}
            className={cn("self-start", actionTextColor[variant])}
            onClick={action.onClick}
          >
            {action.label}
          </Button>
        )}
      </Alert>
    );
  }

  return (
    <Alert
      variant={variant}
      size={size === "xs" ? "xs" : "default"}
      className={cn(
        "flex items-center gap-2",
        dismissible && "pr-8",
        className
      )}
    >
      {dismissButton}
      <div className="flex flex-1 items-start gap-2 min-w-0">
        {icon && (
          <div
            className={cn(
              "flex shrink-0 items-start",
              size !== "xs" && "pt-0.5"
            )}
          >
            {icon}
          </div>
        )}
        <div className="flex flex-col flex-1 min-w-0 gap-0.5">
          {title && <AlertTitle>{title}</AlertTitle>}
          {description && <AlertDescription>{description}</AlertDescription>}
        </div>
      </div>
      {action && (
        <Button
          variant={actionButtonVariant[variant]}
          size={size === "xs" ? "xs" : "sm"}
          className={cn("shrink-0", actionTextColor[variant])}
          onClick={action.onClick}
          disabled={action.disabled}
        >
          {action.label}
        </Button>
      )}
    </Alert>
  );
};

export { NoxusAlert };
export type { NoxusAlertProps, NoxusAlertAction };
